import {
  CloseOutlined,
  EllipsisOutlined,
  ReloadOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { Button, Dropdown, Tabs } from 'antd';
import React, { useEffect, useMemo } from 'react';

/**
 * 自定义标签页渲染（与租户端 poss_tenant_v2.0 同款, 由 app.tsx 的 getCustomTabs 挂接）
 * 使用 useIntl hook 实现完整的国际化支持（包括 locale 切换时实时更新）
 * edit/detail 路由（如 /mall/config/edit/:id）通过截取路径自动匹配 locale key
 */
const TabsBar: React.FC<any> = (props) => {
  const {
    isKeep,
    keepElements,
    navigate,
    closeTab,
    dropLeftTabs,
    dropRightTabs,
    dropOtherTabs,
    refreshTab,
    activeKey,
    tabNameMap,
  } = props;

  const intl = useIntl();

  // 将动态路由的 ID 段去掉，用于识别同一路由模式
  // /mall/config/edit/101 → /mall/config/edit
  const normalizePath = (pn: string): string => {
    const segments = pn.split('/').filter(Boolean);
    if (segments.length > 0 && /^\d+$/.test(segments[segments.length - 1])) {
      return `/${segments.slice(0, -1).join('/')}`;
    }
    return pn;
  };

  // 同一动态路由只保留最新一个标签页（如编辑A → 编辑B，自动关闭编辑A）
  useEffect(() => {
    const currentPn = activeKey?.toLowerCase();
    if (!currentPn) return;

    const normalized = normalizePath(currentPn);
    if (normalized === currentPn) return;

    const toClose = Object.keys(keepElements.current).filter(
      (pn) => pn !== currentPn && normalizePath(pn) === normalized,
    );
    toClose.forEach((pn) => {
      closeTab(pn);
    });
  }, [activeKey]);

  // keep-alive 插件把 keepElements 的 key 统一转成小写（见 @alita/plugins keepalive context.tpl），
  // 而 locale key 保持驼峰（如 menu.accountB.accountBConfiguration），直接查会落空并回退成裸路径。
  // 这里建一份「小写 key → 原始 key」索引，做大小写无关匹配。
  const messageKeyIndex = useMemo(() => {
    const index: Record<string, string> = {};
    Object.keys(intl.messages as Record<string, string>).forEach((key) => {
      index[key.toLowerCase()] = key;
    });
    return index;
  }, [intl.messages]);

  // 根据 pathname 解析国际化标签页名称
  // 从完整路径开始逐级截取，匹配最精确的 locale key
  // 例如 /mall/config/edit/123 → 先试 menu.mall.config.edit.123，再试 menu.mall.config.edit
  const getTabName = (pn: string): string => {
    const segments = pn.split('/').filter(Boolean);
    for (let i = segments.length; i >= 1; i--) {
      const key =
        messageKeyIndex[`menu.${segments.slice(0, i).join('.')}`.toLowerCase()];
      if (key) {
        return intl.formatMessage({ id: key });
      }
    }
    return pn;
  };

  const tabs = Object.entries(keepElements.current);
  const currentKey = `${activeKey?.toLowerCase()}::${tabNameMap[activeKey?.toLowerCase()]}`;

  // 仪表盘固定为不可关闭标签页：侧边栏菜单来自后端 sys_menu 动态拉取，
  // 一旦该菜单记录缺失或被误停用，关掉这个 tab 后将没有任何入口能回到首页。
  const HOME_PATH = '/overview/dashboard';
  const tabItems = tabs.map(([pn, { closable }]: any) => ({
    label: getTabName(pn),
    key: `${pn?.toLowerCase()}::${tabNameMap[pn?.toLowerCase()]}`,
    closable:
      pn?.toLowerCase() === HOME_PATH
        ? false
        : tabs.length === 1
          ? false
          : closable,
  }));

  const dropdownItems = [
    {
      label: intl.formatMessage({
        id: 'tabs.close.left',
        defaultMessage: '关闭左侧',
      }),
      icon: <VerticalRightOutlined />,
      key: 'left',
    },
    {
      label: intl.formatMessage({
        id: 'tabs.close.right',
        defaultMessage: '关闭右侧',
      }),
      icon: <VerticalLeftOutlined />,
      key: 'right',
    },
    {
      label: intl.formatMessage({
        id: 'tabs.close.others',
        defaultMessage: '关闭其他',
      }),
      icon: <CloseOutlined />,
      key: 'others',
    },
    { type: 'divider' as const },
    {
      label: intl.formatMessage({ id: 'tabs.refresh', defaultMessage: '刷新' }),
      icon: <ReloadOutlined />,
      key: 'refresh',
    },
  ];

  const handleAction = ({ key }: any) => {
    const pn = activeKey.toLowerCase();
    switch (key) {
      case 'left':
        dropLeftTabs(pn);
        break;
      case 'right':
        dropRightTabs(pn);
        break;
      case 'others':
        dropOtherTabs(pn);
        break;
      case 'refresh':
        refreshTab(pn);
        break;
    }
  };

  return (
    <div className="runtime-keep-alive-tabs-layout" hidden={!isKeep}>
      <Tabs
        type="editable-card"
        hideAdd
        activeKey={currentKey}
        items={tabItems}
        onChange={(key: string) => {
          const path = key.split('::')[0];
          const entry = keepElements.current[path];
          if (entry) {
            navigate(
              `${entry.location.pathname}${entry.location.search}${entry.location.hash}`,
            );
          }
        }}
        onEdit={(e, action) => {
          if (action === 'remove' && typeof e === 'string') {
            closeTab(e.split('::')[0]);
          }
        }}
        tabBarExtraContent={
          <Dropdown
            menu={{ items: dropdownItems, onClick: handleAction }}
            trigger={['click']}
          >
            <Button
              size="small"
              icon={<EllipsisOutlined />}
              style={{ marginRight: 12 }}
            />
          </Dropdown>
        }
      />
    </div>
  );
};

export default TabsBar;
